<template>
  <div class="cw-flex cw-justify-between cw-border cw-border-b cw-border-gray-200 cw-p-2 cw-cursor-pointer hover:cw-bg-gray-100"
    :class="{ 'cw-bg-gray-100': selectedLocation.id === location.id }"
    role="listitem"
    @click="handleSelectLocation">
    <!-- Left side -->
    <div class="cw-w-fit">
      <!-- Location Name -->
      <h3 class="cw-text-lg cw-font-medium">
        {{ location.name }}
      </h3>

      <!-- Distance Tag - Used In The Find My Location View -->
      <p v-if="location.distance"
        class="cw-w-fit cw-my-1 cw-p-1 cw-font-semibold cw-text-xs cw-rounded cw-bg-gray-200 cw-gray-600">
        {{ location.distance | distance }}
      </p>

      <!-- Extended Location Tag -->
      <p v-if="location.status === 'Extended'"
        class="cw-w-fit cw-my-1 cw-p-1 cw-font-semibold cw-text-xs cw-rounded cw-bg-blue-200 cw-text-blue-700">
        {{ $t('enabled-locations-map.extended-location') }}
      </p>

      <!-- Coming Soon Tag -->
      <p v-if="location.status === 'Deployment'"
        class="cw-w-fit cw-my-1 cw-p-1 cw-font-semibold cw-text-xs cw-rounded cw-bg-blue-200 cw-text-blue-700">
        {{ $t('enabled-locations-map.coming-soon') }}
      </p>

      <!-- Location Address -->
      <address class="cw-mb-4 cw-text-xs">
        {{ location.address.street || '' }} {{ location.address.suburb || '' }}
        <br>
        {{ location.address.city || '' }} {{ location.address.state || '' }} {{ location.address.postcode || '' }}
        <br>
        {{ location.address.country || '' }}
      </address>

      <!-- Links -->
      <div class="cw-text-sm">
        <template v-if="location.status === 'Active'">
          <!-- TODO: It would be nice to include an icon for links that open in a new tab -->
          <a class="cw-block cw-w-fit cw-text-blue-600"
            :href="`https://www.megaport.com/network-latency?locationId=${location.id}`"
            target="_blank">{{ $t('enabled-locations-map.view-latency') }}</a>
          <a class="cw-block cw-w-fit cw-text-blue-600"
            :href="`https://megaport.com/contact/sales?loc=${location.siteCode}&dcName=${location.name}`">
            {{ $t('enabled-locations-map.enquire-about-this-location') }}</a>
        </template>

        <a v-if="location.status === 'Extended'"
          :href="`https://megaport.com/contact/sales?loc=extended&type=extended&dcName=${encodeURI(location.name)}`"
          class="cw-block cw-w-fit cw-text-blue-600">{{ $t('enabled-locations-map.enquire-about-this-location') }}</a>

        <a v-if="location.status === 'Deployment'"
          :href="`https://megaport.com/contact/sales?loc=${location.id}&dcName=${location.name}`"
          class="cw-block cw-w-fit cw-text-blue-600">{{ $t('enabled-locations-map.enquire-about-this-location') }}</a>
      </div>
    </div>

    <!-- Right side -->
    <div class="cw-w-fit cw-top-2 cw-right-2">
      <div class="cw-flex cw-flex-col cw-items-end cw-gap-1 cw-text-sm">
        <img v-if="location.dc.id"
          :src="`https://media.megaport.com/datacentre/${location.dc.id}/62x30.png`"
          onerror="this.style.display='none'"
          width="62"
          class="cw-p-1 cw-mix-blend-darken"
          :alt="location.name">
        <!-- MCR -->
        <div v-if="location.products.mcrVersion === 2"
          class="cw-flex cw-items-center cw-w-full cw-gap-1">
          <mcr-logo class="cw-text-slate-800 cw-fill-current"
            title="MCR Enabled Location" />
          <span class="cw-block cw-whitespace-nowrap">
            {{ $t('enabled-locations-map.mcr-enabled') }}
          </span>
        </div>
        <!-- 100G -->
        <div v-if="location.products.megaport.includes(100)"
          class="cw-flex cw-items-center cw-w-full cw-gap-1">
          <port-logo class="cw-text-slate-800 cw-fill-current"
            title="100G Enabled Location" />
          <span class="cw-block cw-whitespace-nowrap">
            {{ $t('enabled-locations-map.100G-enabled') }}
          </span>
        </div>
        <!-- MVE -->
        <div v-if="'mve' in location.products"
          class="cw-flex cw-items-center cw-w-full cw-gap-1">
          <mve-logo class="text-slate-800 cw-fill-current"
            title="MVE Enabled Location" />
          <span class="cw-block cw-whitespace-nowrap">
            {{ $t('enabled-locations-map.mve-enabled') }}
          </span>
        </div>
      </div>
    </div>
    <slot />
  </div>
</template>

<script>
import MCRLogo from '@/locations-map/assets/MCR.svg'
import PortLogo from '@/locations-map/assets/Megaport.svg'
import MVELogo from '@/locations-map/assets/MVE.svg'

export default {
  name: 'LocationItem',

  components: {
    'mcr-logo': MCRLogo,
    'port-logo': PortLogo,
    'mve-logo': MVELogo,
  },

  filters: {
    distance(value) {
      const km = (value / 1000).toFixed(2)
      const mi = (km * 0.621371).toFixed(2)
      return `${km}km / ${mi}mi`
    },
  },

  props: {
    location: {
      type: Object,
      required: true,
      default: () => {
        return {}
      },
    },
    selectedLocation: {
      type: Object,
      required: false,
      default: () => {
        return {}
      },
    },
  },

  methods: {
    handleSelectLocation() {
      this.$emit('selectLocation', this.location)
    },
  },
}
</script>
